import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';

interface TransactionDateFilterProps {
  onApply: (from?: string, to?: string) => void;
  onClear: () => void;
  disabled?: boolean;
}

const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TransactionDateFilter: React.FC<TransactionDateFilterProps> = ({
  onApply,
  onClear,
  disabled = false,
}) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isApplied, setIsApplied] = useState<boolean>(false);

  const handleApply = () => {
    if (!startDate && !endDate) return;
    const fromStr = startDate ? formatDateToYMD(startDate) : undefined;
    const toStr = endDate ? formatDateToYMD(endDate) : undefined;
    setIsApplied(true);
    onApply(fromStr, toStr);
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setIsApplied(false);
    onClear();
  };

  const hasSelection = Boolean(startDate || endDate);

  return (
    <div
      className="transaction-date-filter"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleApply();
        }
      }}
    >
      <span className="date-filter-label">Date Range:</span>
      <div className="date-filter-inputs">
        <div className="date-filter-picker-wrapper">
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            maxDate={endDate && endDate < new Date() ? endDate : new Date()}
            dateFormat="MMM d, yyyy"
            placeholderText="From"
            className="date-filter-input"
            popperClassName="date-filter-popper"
            popperPlacement="bottom-start"
            portalId="root"
            disabled={disabled}
          />
          <CalendarTodayOutlinedIcon className="date-filter-calendar-icon" />
        </div>
        <div className="date-filter-picker-wrapper">
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate || undefined}
            maxDate={new Date()}
            dateFormat="MMM d, yyyy"
            placeholderText="To"
            className="date-filter-input"
            popperClassName="date-filter-popper"
            popperPlacement="bottom-start"
            portalId="root"
            disabled={disabled}
          />
          <CalendarTodayOutlinedIcon className="date-filter-calendar-icon" />
        </div>
      </div>
      <button
        type="button"
        className="date-filter-apply-btn"
        onClick={handleApply}
        disabled={disabled || !hasSelection}
      >
        Apply
      </button>
      <button
        type="button"
        className="date-filter-clear-btn"
        onClick={handleClear}
        disabled={disabled || (!hasSelection && !isApplied)}
      >
        Clear
      </button>
    </div>
  );
};

export default TransactionDateFilter;
